import { FolderOpen } from "lucide-react";
import { formatDateTime } from "../../utils/applicationFormatters";
import styles from "../../styles/applicationDetail.module.css";
import type { NoteFile } from "../../types/tabs";
import type { NotesTabAdditionalFilesSectionProps } from "../../types";

export function NotesTabAdditionalFilesSection({
  tab,
}: NotesTabAdditionalFilesSectionProps) {
  const {
    applicationData,
    canManageAdditionalFiles,
    fileLabel,
    selectedFiles,
    fileInputRef,
    uploadingAdditionalFiles,
    handleFileSelect,
    handleFileUpload,
    handlePreviewAdditionalFile,
    handleRemoveAdditionalFile,
  } = tab;

  return (
    <div className={styles.section} data-testid="application-notes-tab-section-additional-files">
      <h2 className={styles.sectionTitle} data-testid="application-notes-tab-additional-files-title">
        <span className={styles.sectionTitleIcon} aria-hidden="true">
          <FolderOpen className="h-4 w-4" />
        </span>
        Additional Files Received
      </h2>
      <div className={styles.alertWarning} style={{ marginBottom: "16px" }}>
        <p style={{ margin: 0 }}>
          Use CTRL (windows)/CMD (apple) + Click to select multiple files to
          upload at one time.
        </p>
      </div>

      <div className={styles.infoRow}>
        <div
          className={styles.infoColumn}
          style={{ maxWidth: "min(400px, 100%)" }}
        >
          <div className="flex min-h-[2.5rem] overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm">
            <button
              type="button"
              data-testid="application-notes-tab-choose-file-button"
              disabled={!canManageAdditionalFiles}
              onClick={() => {
                if (canManageAdditionalFiles) fileInputRef.current?.click();
              }}
              aria-label={
                selectedFiles.length > 0
                  ? `Selected: ${fileLabel}`
                  : "Choose file"
              }
              className={`min-w-0 flex-1 truncate px-3 py-2 text-left text-sm transition ${
                selectedFiles.length > 0
                  ? "text-slate-700"
                  : "text-slate-400"
              } hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60`}
            >
              {fileLabel}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              data-testid="application-notes-tab-additional-files-input"
              multiple
              onChange={handleFileSelect}
              className="sr-only"
              disabled={!canManageAdditionalFiles}
            />
            <button
              type="button"
              onClick={() => {
                if (!canManageAdditionalFiles || uploadingAdditionalFiles) {
                  return;
                }
                if (selectedFiles.length === 0) {
                  fileInputRef.current?.click();
                  return;
                }
                void handleFileUpload();
              }}
              disabled={uploadingAdditionalFiles || !canManageAdditionalFiles}
              data-testid="application-notes-tab-upload-files-button"
              aria-label={
                selectedFiles.length > 0
                  ? "Upload selected files"
                  : "Choose files to upload"
              }
              className="shrink-0 border-l border-slate-300 bg-[rgb(21_151_255)]/10 px-4 py-2 text-sm font-semibold text-[rgb(21_151_255)] transition hover:bg-[rgb(21_151_255)]/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[rgb(21_151_255)]/40 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {uploadingAdditionalFiles ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>

      {applicationData?.addition_files_received &&
        applicationData.addition_files_received.length > 0 && (
          <div className={styles.filesList} style={{ marginTop: "16px" }}>
            {applicationData.addition_files_received.map(
              (file: NoteFile, index: number) => (
                <div
                  key={index}
                  className={styles.fileItem}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                  data-testid={`application-notes-tab-additional-file-${index}`}
                >
                  {file.filepath ? (
                    <>
                      <button
                        type="button"
                        className={styles.link}
                        onClick={() => handlePreviewAdditionalFile(file)}
                        data-testid={`application-notes-tab-additional-file-${index}-link`}
                      >
                        {file.filename}
                        {file.name ? ` - ${file.name}` : ""}
                        {file.created_at
                          ? ` - ${formatDateTime(file.created_at)}`
                          : ""}
                      </button>
                      <button
                        type="button"
                        className={styles.btnSmall}
                        style={{
                          padding: "4px 8px",
                          fontSize: "11px",
                          background: "#ef4444",
                        }}
                        onClick={() =>
                          handleRemoveAdditionalFile(file.attachment_id ?? "")
                        }
                        disabled={!canManageAdditionalFiles}
                        data-testid={`application-notes-tab-additional-file-${index}-remove-button`}
                      >
                        Remove
                      </button>
                    </>
                  ) : null}
                </div>
              ),
            )}
          </div>
        )}
    </div>
  );
}
